import React, { useEffect, useState } from 'react';
import BrokerLayout from '@/components/BrokerLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Upload, Check, ShieldCheck } from 'lucide-react';

const BrokerKYC = () => {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get('/broker/profile'),
        api.get('/public/cities')
      ]);
      setProfile(p.data);
      setCities(c.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleField = (field, value) => setProfile({ ...profile, [field]: value });

  const handleUpload = async (field, file) => {
    if (!file) return;
    setUploading({ ...uploading, [field]: true });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile({ ...profile, [field]: data.path });
      toast.success('Uploaded successfully');
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading({ ...uploading, [field]: false });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/broker/profile', profile);
      toast.success('Profile saved');
      await load();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.put('/broker/profile', profile);
      await api.post('/broker/submit');
      toast.success('Profile submitted for admin review!');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <BrokerLayout><div>Loading...</div></BrokerLayout>;

  const isSubmitted = ['submitted', 'under_review', 'approved'].includes(profile.status);
  const isApproved = profile.status === 'approved';

  return (
    <BrokerLayout profile={profile}>
      <div className="max-w-4xl">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F2C59]" data-testid="kyc-title">KYC & Profile</h1>
            <p className="text-sm text-slate-500 mt-1">Complete your profile to get verified and start listing projects</p>
          </div>
          {isApproved && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md text-xs font-semibold">
              <ShieldCheck size={14}/> Approved
            </div>
          )}
        </div>

        <div className="mb-6 bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-700">Profile Completion</span>
            <span className="font-mono font-bold text-[#0F2C59]">{profile.profile_completion || 0}%</span>
          </div>
          <Progress value={profile.profile_completion || 0} className="h-2 mt-2" />
        </div>

        {/* Personal Info */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-4">
          <h2 className="font-display font-bold text-lg text-[#0F2C59] mb-4">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={profile.full_name || ''} onChange={(e) => handleField('full_name', e.target.value)} disabled={isSubmitted} data-testid="kyc-fullname-input" />
            </div>
            <div>
              <Label>WhatsApp Number</Label>
              <Input value={profile.whatsapp_number || ''} onChange={(e) => handleField('whatsapp_number', e.target.value)} placeholder="+91..." data-testid="kyc-whatsapp-input" />
            </div>
            <div>
              <Label>City *</Label>
              <Select value={profile.city || ''} onValueChange={(v) => handleField('city', v)} disabled={isSubmitted}>
                <SelectTrigger data-testid="kyc-city-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{cities.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>State</Label>
              <Input value={profile.state || ''} onChange={(e) => handleField('state', e.target.value)} data-testid="kyc-state-input" />
            </div>
            <div>
              <Label>Pincode</Label>
              <Input value={profile.pincode || ''} onChange={(e) => handleField('pincode', e.target.value)} data-testid="kyc-pincode-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Textarea value={profile.address || ''} onChange={(e) => handleField('address', e.target.value)} rows={2} data-testid="kyc-address-input" />
            </div>
          </div>
        </div>

        {/* Professional */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-4">
          <h2 className="font-display font-bold text-lg text-[#0F2C59] mb-4">Professional Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Broker Type *</Label>
              <Select value={profile.broker_type} onValueChange={(v) => handleField('broker_type', v)}>
                <SelectTrigger data-testid="kyc-brokertype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="agency">Agency / Company</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Agency / Company Name</Label>
              <Input value={profile.agency_name || ''} onChange={(e) => handleField('agency_name', e.target.value)} data-testid="kyc-agency-input" />
            </div>
            <div>
              <Label>Primary Operating City *</Label>
              <Select value={profile.primary_city || ''} onValueChange={(v) => handleField('primary_city', v)}>
                <SelectTrigger data-testid="kyc-primary-city-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{cities.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Years of Experience</Label>
              <Input type="number" value={profile.years_of_experience || 0} onChange={(e) => handleField('years_of_experience', parseInt(e.target.value) || 0)} data-testid="kyc-experience-input" />
            </div>
            <div>
              <Label>RERA Registration Number *</Label>
              <Input value={profile.rera_number || ''} onChange={(e) => handleField('rera_number', e.target.value)} placeholder="RERA/XX/XXXX" data-testid="kyc-rera-input" />
            </div>
            <div>
              <Label>GST Number</Label>
              <Input value={profile.gst_number || ''} onChange={(e) => handleField('gst_number', e.target.value)} data-testid="kyc-gst-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Specializations (comma separated)</Label>
              <Input
                value={(profile.specializations || []).join(', ')}
                onChange={(e) => handleField('specializations', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="Residential, Commercial, Plot"
                data-testid="kyc-specialization-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Professional Bio</Label>
              <Textarea value={profile.bio || ''} onChange={(e) => handleField('bio', e.target.value)} rows={4} placeholder="Tell buyers about your experience..." data-testid="kyc-bio-input" />
            </div>
          </div>
        </div>

        {/* KYC Documents */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-4">
          <h2 className="font-display font-bold text-lg text-[#0F2C59] mb-2">KYC Documents</h2>
          <p className="text-xs text-slate-500 mb-4">All documents are encrypted and never shared publicly.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { field: 'pan_document_url', label: 'PAN Card' },
              { field: 'aadhaar_document_url', label: 'Aadhaar' },
              { field: 'rera_certificate_url', label: 'RERA Certificate' },
              { field: 'address_proof_url', label: 'Address Proof' }
            ].map(doc => (
              <div key={doc.field} className="border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="!mb-0">{doc.label}</Label>
                  {profile[doc.field] && <Check size={16} className="text-emerald-600" />}
                </div>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md py-3 cursor-pointer hover:border-[#F97316] text-sm text-slate-600" data-testid={`upload-${doc.field}`}>
                  <Upload size={14} />
                  {uploading[doc.field] ? 'Uploading...' : profile[doc.field] ? 'Replace file' : 'Upload'}
                  <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => handleUpload(doc.field, e.target.files[0])} />
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 items-center">
          <Button onClick={save} disabled={saving} variant="outline" data-testid="save-kyc-btn">
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          {!isSubmitted && (
            <Button onClick={submit} disabled={saving} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="submit-kyc-btn">
              Submit for Approval
            </Button>
          )}
          {isSubmitted && !isApproved && (
            <div className="text-sm text-amber-700 font-medium">⏳ Awaiting admin review</div>
          )}
        </div>

        {profile.admin_notes && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="text-xs font-semibold uppercase text-red-700 mb-1">Admin Feedback</div>
            <div className="text-sm text-red-800">{profile.admin_notes}</div>
          </div>
        )}
      </div>
    </BrokerLayout>
  );
};

export default BrokerKYC;
